/**
 * The v5 order on the client: what the marketing manager chose, as the API takes it. Money, the split, the promise,
 * dates and readiness all come back from POST /v5/plan; nothing here prices anything.
 */
import type { OrderPrepare } from '../types';
import type { Catalogue, Creative, OrderV5Body, PartId, V5Idea } from './types';

export interface V5State {
  brief: string; files: string[]; productName: string;
  outcome: string | null; parts: PartId[]; goals: Partial<Record<PartId, string>>; usedSuggestion: boolean;
  runs: { boost: 'beatly' | 'own'; social: 'beatly' | 'own' };
  market: string; currency: string; start: string; weeks: number; budget: number; weights: Partial<Record<PartId, number>>;
  packages: { ugc: 'starter' | 'growth' | 'scale'; seed: 'starter' | 'growth' | 'scale' };
  audienceIds: string[]; audienceText: string;
  /** the client's own concept (option 1); idea = a twist or Lykke idea they picked instead, null = their concept */
  concept: { title: string; description: string; keyMessage: string }; idea: V5Idea | null;
  creative: Creative; delivery: string; seedGift: string; embargo: 'launch' | 'free' | null; ambTags: string; ugcUse: string[];
  approverEmail: string; approverName: string; engagement: 'retainer' | 'adhoc'; pay: 'card' | 'invoice'; po: string;
}

export const STEPS = ['Outcome', 'Mix', 'Plan', 'Review and order'] as const;
export type Step = 0 | 1 | 2 | 3;

const addDays = (iso: string, n: number) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

export function initialV5(prep: OrderPrepare, cat: Catalogue): V5State {
  const product = prep.products[0];
  return {
    brief: '', files: [], productName: product?.name || '',
    outcome: null, parts: [], goals: {}, usedSuggestion: false, runs: { boost: 'beatly', social: 'beatly' },
    market: prep.defaults.market, currency: prep.defaults.currency, start: addDays(today(), 7 + 7 * cat.complexDeliveryWeeks), weeks: 8,
    budget: Math.max(prep.defaults.budget || 0, 300000), weights: {}, packages: { ugc: 'starter', seed: 'growth' },
    audienceIds: product ? prep.audiences.filter((a) => a.productIds.includes(product.id)).map((a) => a.id).slice(0, 2) : prep.audiences.slice(0, 2).map((a) => a.id),
    audienceText: '', concept: { title: '', description: '', keyMessage: '' }, idea: null,
    creative: 'hub', delivery: 'Shipped by client', seedGift: product ? `1 ${product.name} and a card` : '', embargo: 'launch', ambTags: '', ugcUse: [],
    approverEmail: prep.me.email, approverName: prep.me.name, engagement: cat.engagement || 'retainer', pay: 'card', po: '',
  };
}

/** The parts in catalogue order; a boost on its own pulls in influencer marketing (it boosts creator posts). */
export function withParts(cat: Catalogue, list: PartId[]): PartId[] {
  const set = new Set(list);
  for (const p of cat.parts) if (set.has(p.id) && p.needs && !p.needs.some((n) => set.has(n))) set.add('inf');
  return cat.parts.map((p) => p.id).filter((id) => set.has(id));
}
export const goalOf = (cat: Catalogue, s: V5State, id: PartId) => s.goals[id] || cat.parts.find((p) => p.id === id)!.goals[0].id;

/** The media planner preset the ideas prompt knows, for an outcome. */
export const PRESET_FOR: Record<string, string> = { Awareness: 'launch-awareness', Consideration: 'traffic-consideration', 'Conversion & sales': 'conversion-performance', 'Content production (UGC)': 'ugc-content-engine', 'Product launch': 'launch-awareness', 'Always-on': 'always-on-ambassador' };

export function bodyV5(s: V5State, prep: OrderPrepare, cat: Catalogue): OrderV5Body {
  const auds = prep.audiences.filter((a) => s.audienceIds.includes(a.id));
  const ages = auds.flatMap((a) => [a.ageMin, a.ageMax]).filter((n): n is number => typeof n === 'number');
  const goals: Partial<Record<PartId, string>> = {};
  for (const id of s.parts) goals[id] = goalOf(cat, s, id);
  return {
    brandId: prep.brand.id, brandName: prep.brand.name, product: s.productName.trim() || undefined, brief: s.brief.trim() || undefined,
    outcome: s.outcome || '', parts: s.parts, goals, runs: s.runs, market: s.market, currency: s.currency, start: s.start, weeks: s.weeks, budget: s.budget, weights: s.weights,
    packages: s.packages, audienceText: [...auds.map((a) => a.name), s.audienceText.trim()].filter(Boolean).join(', ') || undefined,
    ageMin: ages.length ? Math.min(...ages) : undefined, ageMax: ages.length ? Math.max(...ages) : undefined, gender: auds[0]?.gender,
    idea: chosenIdea(s) || undefined,
    creative: s.creative, delivery: s.delivery || undefined, seedGift: s.seedGift.trim() || undefined, embargo: s.embargo || undefined,
    ambTags: s.ambTags.trim() || undefined, ugcUse: s.ugcUse, approver: { name: s.approverName, email: s.approverEmail },
    engagement: s.engagement, pay: s.pay, po: s.po.trim() || undefined,
  };
}

/** The idea the order carries: the twist or Lykke idea they picked, else their own concept once it has a name. */
export function chosenIdea(s: Pick<V5State, 'concept' | 'idea'>): V5Idea | null {
  if (s.idea) return s.idea;
  const c = s.concept;
  return c.title.trim() ? { title: c.title.trim(), description: c.description.trim() || undefined, keyMessage: c.keyMessage.trim() || undefined, source: 'client' } : null;
}

export const fmt = (n: number) => Math.round(n).toLocaleString('sv-SE').replace(/\s/g, '\u00a0');
export const short = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1).replace('.0', '')}M` : `${Math.round(n / 1e3)}k`);
/** ISO dates inside a sentence from the API, written the way people read them. */
export const days = (text: string) => text.replace(/\b\d{4}-\d{2}-\d{2}\b/g, (m) => day(m));
export const day = (iso: string) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '');
export const addDaysIso = addDays;
